// Profile Page Component
// Displays user stats and allows account management

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useUsername, validateUsername } from '../hooks/useUsername';
import { validatePassword } from '../services/authService';
import { getUserStats, formatTime } from '../services/leaderboardService';
import { readPlays } from '../services/playHistory';
import PlayHistory from '../components/PlayHistory/PlayHistory';
import EmailSetting from '../components/EmailSetting/EmailSetting';
import ProfileRooms from '../components/ProfileRooms/ProfileRooms';
import AvatarSelectModal from '../components/AvatarSelectModal/AvatarSelectModal';
import Avatar from '../components/Avatar/Avatar';
import './Profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const { username, avatar, hasUsername, isLoading: usernameLoading, isAuthenticated, register, logout, checkAvailability, updateAvatar, rename } = useUsername();
  const [stats, setStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Avatar change state
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [isSavingAvatar, setIsSavingAvatar] = useState(false);

  // Copy username state
  const [copied, setCopied] = useState(false);

  // Username change state
  const [isChangingUsername, setIsChangingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changeStep, setChangeStep] = useState(1); // 1 = warning, 2 = username, 3 = password
  const [changeError, setChangeError] = useState('');
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load user stats
  useEffect(() => {
    const loadStats = async () => {
      if (username) {
        setIsLoadingStats(true);
        const userStats = await getUserStats(username);
        setStats(userStats);
        setIsLoadingStats(false);
      } else {
        setStats(null);
        setIsLoadingStats(false);
      }
    };

    if (!usernameLoading) {
      loadStats();
    }
  }, [username, usernameLoading]);

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Handle copy username
  const handleCopyUsername = async () => {
    try {
      await navigator.clipboard.writeText(username);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Handle avatar change
  const handleAvatarSelect = async (newAvatar) => {
    setIsSavingAvatar(true);
    const result = await updateAvatar(newAvatar);
    setIsSavingAvatar(false);

    if (result.success) {
      setShowAvatarModal(false);
    }
  };

  // Start username change process
  const handleStartUsernameChange = () => {
    setIsChangingUsername(true);
    setChangeStep(1); // Start with warning
    setNewUsername('');
    setNewPassword('');
    setConfirmPassword('');
    setChangeError('');
  };

  // Cancel username change
  const handleCancelChange = () => {
    setIsChangingUsername(false);
    setChangeStep(1);
    setNewUsername('');
    setNewPassword('');
    setConfirmPassword('');
    setChangeError('');
  };

  // Proceed from warning to username input
  const handleProceedFromWarning = () => {
    setChangeStep(2);
  };

  // Check username availability and proceed to password
  const handleCheckUsername = async () => {
    setChangeError('');

    const validation = validateUsername(newUsername);
    if (!validation.valid) {
      setChangeError(validation.error);
      return;
    }

    setIsCheckingUsername(true);
    const result = await checkAvailability(newUsername.trim());
    setIsCheckingUsername(false);

    if (!result.available && newUsername.trim().toLowerCase() !== String(username).toLowerCase()) {
      setChangeError(result.error || 'Username is already taken');
      return;
    }

    // The same account under the new name: scores, rooms and races move with
    // it and the password stays (it used to make a new, empty account).
    setIsCheckingUsername(true);
    const renamed = await rename(newUsername.trim());
    setIsCheckingUsername(false);
    if (!renamed.success) { setChangeError(renamed.error || 'Could not change the name'); return; }
    setIsChangingUsername(false);
    setChangeStep(1);
    setNewUsername('');
  };

  // Create new account with new username
  const handleCreateNewAccount = async () => {
    setChangeError('');

    // Validate password
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setChangeError(passwordValidation.error);
      return;
    }

    if (newPassword !== confirmPassword) {
      setChangeError('Passwords do not match');
      return;
    }

    setIsSaving(true);

    // Register new account
    const result = await register(newUsername.trim(), newPassword);

    if (result.success) {
      setIsChangingUsername(false);
      setChangeStep(1);
      setNewUsername('');
      setNewPassword('');
      setConfirmPassword('');
      // Stats will reload via useEffect
    } else {
      setChangeError(result.error || 'Failed to create new account');
    }

    setIsSaving(false);
  };

  if (usernameLoading) {
    return (
      <>
        <div className="profile-container">
          <div className="profile-loading">
            <div className="loading-spinner"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </>
    );
  }

  // Not authenticated - show login prompt
  if (!isAuthenticated || !hasUsername) {
    return (
      <>
        <div className="profile-container">
          <div className="profile-card profile-setup">
            <div className="profile-header">
              <div className="profile-icon">🏴‍☠️</div>
              <h1>Join the Crew!</h1>
              <p className="profile-subtitle">Log in or create an account to track your scores</p>
            </div>

            <div className="auth-buttons">
              <Link to="/login" className="auth-btn login-btn">
                <span className="btn-icon">🔑</span>
                Log In
              </Link>
              <p className="or-divider">or play as guest</p>
              <Link to="/" className="auth-btn guest-btn">
                <span className="btn-icon">🎮</span>
                Play Without Account
              </Link>
            </div>

            <p className="auth-note">
              Your scores will be saved to the leaderboard when you create an account after playing!
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Your Profile - One Piece Trivia</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="profile-container">
        <div className="profile-card">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="wanted-tag">WANTED</div>
          <div className="profile-avatar-container">
            <div className="profile-avatar">
              {stats?.isGrandMaster ? stats.grandMasterIcon : <Avatar value={avatar} alt="" />}
            </div>
            <button
              className="change-avatar-btn"
              onClick={() => setShowAvatarModal(true)}
              title="Change Avatar"
            >
              <i className="fa fa-pencil"></i>
            </button>
          </div>

          <div className="username-row">
            <h1 className="profile-username">{username}</h1>
            <button
              className={`copy-username-btn ${copied ? 'copied' : ''}`}
              onClick={handleCopyUsername}
              title="Copy username"
            >
              <i className={`fa ${copied ? 'fa-check' : 'fa-copy'}`}></i>
            </button>
          </div>
          {/* A trivia bounty: a million berries for every point of their best runs */}
          <div className="profile-bounty" title="A million berries for every point in your best run of each game">
            <span className="bounty-mark">฿</span>
            {(((stats?.characterBest?.score || 0) + (stats?.imageBest?.score || 0) + (stats?.devilfruitBest?.score || 0) + (stats?.bountyBest?.score || 0)) * 1000000).toLocaleString('en-US')}
          </div>
          {stats?.isGrandMaster && (
            <div className="grand-master-badge">
              {stats.grandMasterIcon} {stats.grandMasterTier}
            </div>
          )}

          <div className="profile-actions">
            <button className="edit-username-btn" onClick={handleStartUsernameChange}>
              Change Username
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              <i className="fa fa-sign-out"></i> Logout
            </button>
          </div>
        </div>

        {/* Avatar Selection Modal */}
        <AvatarSelectModal
          isOpen={showAvatarModal}
          currentAvatar={avatar}
          onSelect={handleAvatarSelect}
          onClose={() => setShowAvatarModal(false)}
          isSaving={isSavingAvatar}
        />

        {/* Username Change Modal */}
        {isChangingUsername && (
          <div className="username-change-overlay" onClick={handleCancelChange}>
            <div className="username-change-modal" onClick={(e) => e.stopPropagation()}>

              {/* Step 1: Warning */}
              {changeStep === 1 && (
                <>
                  <div className="modal-header warning">
                    <span className="modal-icon">✏️</span>
                    <h2>Change your name</h2>
                  </div>
                  <div className="modal-body">
                    <p className="warning-text">
                      Your account keeps <strong>everything</strong> under the new name.
                    </p>
                    <ul className="warning-list">
                      <li>Your scores and leaderboard places move to the new name</li>
                      <li>Your rooms, races and times move too</li>
                      <li>Your password stays the same</li>
                    </ul>
                    <p className="warning-question">Are you sure you want to continue?</p>
                  </div>
                  <div className="modal-actions">
                    <button className="proceed-btn" onClick={handleProceedFromWarning}>
                      Continue
                    </button>
                    <button className="cancel-btn" onClick={handleCancelChange}>
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {/* Step 2: New Username */}
              {changeStep === 2 && (
                <>
                  <div className="modal-header">
                    <span className="modal-icon">🏴‍☠️</span>
                    <h2>Choose New Username</h2>
                  </div>
                  <div className="modal-body">
                    <div className="input-group">
                      <input
                        type="text"
                        value={newUsername}
                        onChange={(e) => {
                          setNewUsername(e.target.value);
                          if (changeError) setChangeError('');
                        }}
                        placeholder="New pirate name..."
                        maxLength={15}
                        className={changeError ? 'has-error' : ''}
                        autoFocus
                        disabled={isCheckingUsername}
                      />
                      <span className="char-count">{newUsername.length}/15</span>
                    </div>

                    {changeError && (
                      <div className="error-message">
                        <span>⚠️</span> {changeError}
                      </div>
                    )}

                    <p className="input-hint">3-15 characters, letters, numbers, underscores only</p>
                  </div>
                  <div className="modal-actions">
                    <button
                      className="proceed-btn"
                      onClick={handleCheckUsername}
                      disabled={isCheckingUsername || newUsername.trim().length < 3}
                    >
                      {isCheckingUsername ? 'Saving…' : 'Change name'}
                    </button>
                    <button className="cancel-btn" onClick={handleCancelChange} disabled={isCheckingUsername}>
                      Cancel
                    </button>
                  </div>
                </>
              )}

              {/* Step 3: Password */}
              {changeStep === 3 && (
                <>
                  <div className="modal-header">
                    <span className="modal-icon">🔐</span>
                    <h2>Create Password</h2>
                    <p className="modal-subtitle">for "{newUsername}"</p>
                  </div>
                  <div className="modal-body">
                    <div className="input-group">
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          if (changeError) setChangeError('');
                        }}
                        placeholder="Password (min 6 characters)"
                        className={changeError ? 'has-error' : ''}
                        autoFocus
                        disabled={isSaving}
                      />
                    </div>
                    <div className="input-group">
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (changeError) setChangeError('');
                        }}
                        placeholder="Confirm password"
                        className={changeError ? 'has-error' : ''}
                        disabled={isSaving}
                      />
                    </div>

                    {changeError && (
                      <div className="error-message">
                        <span>⚠️</span> {changeError}
                      </div>
                    )}
                  </div>
                  <div className="modal-actions">
                    <button
                      className="proceed-btn"
                      onClick={handleCreateNewAccount}
                      disabled={isSaving || newPassword.length < 6 || confirmPassword.length < 6}
                    >
                      {isSaving ? 'Creating Account...' : 'Create New Account'}
                    </button>
                    <button className="cancel-btn" onClick={handleCancelChange} disabled={isSaving}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Stats Section */}
        <div className="profile-stats">
          <h2 className="stats-title">Your Best Scores</h2>

          {isLoadingStats ? (
            <div className="stats-loading">
              <div className="loading-spinner small"></div>
              <p>Loading stats...</p>
            </div>
          ) : (
            <div className="stats-grid">
              {/* Character Quiz */}
              <div className="stat-card">
                <img src="/character-quiz-and-any-related.png" alt="Character Quiz" className="stat-icon-img" />
                <h3>Character Quiz</h3>
                {stats?.characterBest ? (
                  <>
                    <div className="stat-score">{stats.characterBest.score}/15</div>
                    <div className="stat-details">
                      <span>Time: {formatTime(stats.characterBest.time)}</span>
                    </div>
                  </>
                ) : (
                  <div className="stat-empty">Not played yet</div>
                )}
              </div>

              {/* Image Quiz */}
              <div className="stat-card">
                <img src="/image-quiz-and-any-related.png" alt="" className="stat-icon-img" />
                <h3>Image Quiz</h3>
                {stats?.imageBest ? (
                  <>
                    <div className="stat-score">{stats.imageBest.score}/15</div>
                    <div className="stat-details">
                      <span>Time: {formatTime(stats.imageBest.time)}</span>
                    </div>
                  </>
                ) : (
                  <div className="stat-empty">Not played yet</div>
                )}
              </div>

              {/* Devil Fruit Quiz */}
              <div className="stat-card">
                <img src="/devil-fruit-quiz-and-any-related.png" alt="Devil Fruit Quiz" className="stat-icon-img" />
                <h3>Devil Fruit Quiz</h3>
                {stats?.devilfruitBest ? (
                  <>
                    <div className="stat-score">{stats.devilfruitBest.score}/15</div>
                    <div className="stat-details">
                      <span>Time: {formatTime(stats.devilfruitBest.time)}</span>
                    </div>
                  </>
                ) : (
                  <div className="stat-empty">Not played yet</div>
                )}
              </div>

              {/* Bounty Battle */}
              <div className="stat-card">
                <span className="stat-icon-img stat-icon-emoji" aria-hidden="true">💰</span>
                <h3>Bounty Battle</h3>
                {stats?.bountyBest ? (
                  <>
                    <div className="stat-score">{stats.bountyBest.score}/11</div>
                    <div className="stat-details">
                      <span>Time: {formatTime(stats.bountyBest.time)}</span>
                    </div>
                  </>
                ) : (
                  <div className="stat-empty">Not played yet</div>
                )}
              </div>

              {/* Total Games */}
              <div className="stat-card total-games">
                <img src="/total-games.png" alt="Total Games" className="stat-icon-img" />
                <h3>Total Games</h3>
                <div className="stat-score">{stats?.totalGamesPlayed || 0}</div>
              </div>
            </div>
          )}

          <div className="profile-history">
            <div className="profile-history-head">
              <h2 className="stats-title">Your Games</h2>
              <Link to="/history" className="profile-history-all">See all</Link>
            </div>
            <PlayHistory plays={readPlays()} limit={5} />
          </div>

          {/* Grand Master Progress */}
          {!stats?.isGrandMaster && (
            <div className="grand-master-progress">
              <div className="gm-progress-header">
                <img src="/favonepie.png" alt="Grand Master" className="gm-progress-icon" />
                <h3>Grand Master Progress</h3>
              </div>
              <p className="progress-hint">Score 11+ in all 4 games to become a Grand Master!</p>
              {(() => {
                const done = ['characterBest', 'imageBest', 'devilfruitBest', 'bountyBest'].filter((k) => stats?.[k]?.score >= 11).length;
                return (
                  <div className="gm-bar" aria-label={`${done} of 4 games done`}>
                    <div className="gm-bar-fill" style={{ width: `${(done / 4) * 100}%` }} />
                    <span className="gm-bar-text">{done} / 4 games</span>
                  </div>
                );
              })()}
              <div className="progress-checklist">
                <div className={`progress-item ${stats?.characterBest?.score >= 11 ? 'completed' : ''}`}>
                  <img src="/character-quiz-and-any-related.png" alt="Character" className="progress-quiz-icon" />
                  <span className="progress-label">Character Quiz</span>
                  <span className="progress-score">{stats?.characterBest?.score || 0}/11</span>
                  <span className="progress-status">{stats?.characterBest?.score >= 11 ? '✓' : ''}</span>
                </div>
                <div className={`progress-item ${stats?.imageBest?.score >= 11 ? 'completed' : ''}`}>
                  <img src="/image-quiz-and-any-related.png" alt="" className="progress-quiz-icon" />
                  <span className="progress-label">Image Quiz</span>
                  <span className="progress-score">{stats?.imageBest?.score || 0}/11</span>
                  <span className="progress-status">{stats?.imageBest?.score >= 11 ? '✓' : ''}</span>
                </div>
                <div className={`progress-item ${stats?.devilfruitBest?.score >= 11 ? 'completed' : ''}`}>
                  <img src="/devil-fruit-quiz-and-any-related.png" alt="Devil Fruit" className="progress-quiz-icon" />
                  <span className="progress-label">Devil Fruit Quiz</span>
                  <span className="progress-score">{stats?.devilfruitBest?.score || 0}/11</span>
                  <span className="progress-status">{stats?.devilfruitBest?.score >= 11 ? '✓' : ''}</span>
                </div>
                <div className={`progress-item ${stats?.bountyBest?.score >= 11 ? 'completed' : ''}`}>
                  <span className="progress-quiz-icon" style={{ fontSize: '1.4rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>💰</span>
                  <span className="progress-label">Bounty Battle</span>
                  <span className="progress-score">{stats?.bountyBest?.score || 0}/11</span>
                  <span className="progress-status">{stats?.bountyBest?.score >= 11 ? '✓' : ''}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <ProfileRooms />
        <EmailSetting />
      </div>
      </div>
    </>
  );
};

export default Profile;
